import { I18N } from "oc-I18N";

import { ellipse, horizontalLine } from "./MouseAvatar";

type StepName =
  | "initial"
  | "rotate"
  | "rotate_check"
  | "rotate_success"
  | "translate"
  | "translate_check"
  | "translate_success"
  | "zoom"
  | "zoom_check"
  | "zoom_success"
  | "passed"
  | "final";

type MouseStepButton = {
  label: () => string;
  className: string;
  target?: string;
  key?: string;
  autoFocus?: boolean;
};

export type MouseStep = {
  name?: StepName;
  duration?: number;
  enableZaunSelection?: boolean;
  dialog?: {
    key?: string;
    text: () => JSX.Element;
    buttons?: MouseStepButton[];
  };
  mouseAnimation?: {
    className?: string;
    waitBegin?: number;
    action?: (stepengine: any) => Promise<void>;
    waitEnd?: number;
    repeat?: number;
  };
  action?: (stepengine: any) => Promise<void>;
  target?: any;
};

//region Buttons
const ButtonWeiter = {
  label: () => I18N("tutorial_Weiter"),
  className: "outlined-button",
  // target
};

const ButtonTutorialÜberspringen = {
  label: () => I18N("tutorial_Anleitung überspringen"),
  className: "outlined-button",
  target: "final",
};

const ButtonAnimationNochmal = {
  label: () => I18N("tutorial_Nochmal zeigen"),
  className: "outlined-button",
  // target
};
//endregion

//region Actions
const targetIfUserTakesControl = (target: StepName) => async (stepEngine: any) => {
  const cleanup = stepEngine.mouse.userTakesControlAction(() => stepEngine.setStep(target));
  stepEngine.onStateExit(cleanup);
};
//endregion

//region Dialogs
const SehrGutDialogStep = {
  duration: 2000,
  enableZaunSelection: false,
  dialog: {
    text: () => <p>{I18N("tutorial_Sehr gut!")} 👍</p>,
    buttons: [],
  },
};

const zoomDialog = {
  key: "zoom",
  text: () => <p>{I18N("tutorial_Zoomen Sie jetzt bitte die Zaunanlage")}</p>,
  buttons: [ButtonTutorialÜberspringen],
};
//endregion

/**
 * Jeder Step ist ein State einer StateMachine.
 * Mit name können Steps gekennzeichnet und mit target angesprungen werden.
 * Wenn ein Step eine duration hat, dann wird nach Ablauf der folgende Step oder target aufgerufen,
 * sonst verharrt der Step bis zum Auftreten eines Events.
 */
export const STEPS: MouseStep[] = [
  {
    name: "initial",
    duration: 500,
    enableZaunSelection: false,
  },
  {
    // duration: 3000,
    enableZaunSelection: false,
    dialog: {
      text: () => (
        <p>
          {I18N("tutorial_Willkommen beim Zaunplaner.")}
          <br />
          {I18N("tutorial_Ich zeige Ihnen kurz, wie die Bedienung funktioniert.")}
        </p>
      ),
      buttons: [
        ButtonTutorialÜberspringen,
        {
          ...ButtonWeiter,
          key: "next-primary",
          target: "rotate",
          autoFocus: true,
          className: "primary-button",
        },
      ],
    },
  },
  {
    name: "rotate",
    enableZaunSelection: false,
    dialog: {
      key: "rotate",
      text: () => <p>{I18N("tutorial_Drehen Sie jetzt bitte den Boden")}</p>,
      buttons: [ButtonTutorialÜberspringen],
    },
    mouseAnimation: {
      className: "left-press",
      waitBegin: 500,
      action: async (stepEngine) => await stepEngine.mouse.rotateAction(ellipse, 2000),
      waitEnd: 500,
      repeat: 2,
    },
    action: targetIfUserTakesControl("rotate_check"),
  },
  {
    name: "rotate_check",
    enableZaunSelection: false,
    dialog: {
      key: "rotate",
      text: () => <p>{I18N("tutorial_Drehen Sie jetzt bitte den Boden")}</p>,
      buttons: [ButtonTutorialÜberspringen, { ...ButtonAnimationNochmal, target: "rotate" }],
    },
    action: async (stepEngine) => {
      const cleanup = stepEngine.mouse.orbitControlsSolvedAction(
        [
          APP.controls.STATE.ROTATE,
          APP.controls.STATE.TOUCH_ROTATE,
          // APP.controls.STATE.TOUCH_DOLLY_PAN,
          // APP.controls.STATE.TOUCH_DOLLY_ROTATE
        ],
        100,
        () => stepEngine.setStep("rotate_success")
      );
      stepEngine.onStateExit(cleanup);
    },
  },
  {
    ...SehrGutDialogStep,
    name: "rotate_success",
  },
  {
    name: "translate",
    enableZaunSelection: false,
    dialog: {
      key: "translate",
      text: () => <p>{I18N("tutorial_Verschieben Sie jetzt bitte den Boden")}</p>,
      buttons: [ButtonTutorialÜberspringen],
    },
    mouseAnimation: {
      className: "right-press",
      waitBegin: 100,
      action: async (stepEngine) => await stepEngine.mouse.translate(horizontalLine, 2000),
      waitEnd: 500,
      repeat: 2,
    },
    action: targetIfUserTakesControl("translate_check"),
  },
  {
    name: "translate_check",
    enableZaunSelection: false,
    dialog: {
      key: "translate",
      text: () => <p>{I18N("tutorial_Verschieben Sie jetzt bitte den Boden")}</p>,
      buttons: [ButtonTutorialÜberspringen, { ...ButtonAnimationNochmal, target: "translate" }],
    },
    action: async (stepEngine) => {
      const cleanup = stepEngine.mouse.orbitControlsSolvedAction(
        [APP.controls.STATE.PAN, APP.controls.STATE.TOUCH_PAN],
        100,
        () => stepEngine.setStep("translate_success")
      );
      stepEngine.onStateExit(cleanup);
    },
  },
  {
    ...SehrGutDialogStep,
    name: "translate_success",
  },
  {
    name: "zoom",
    duration: 100,
    enableZaunSelection: false,
    dialog: zoomDialog,
    mouseAnimation: {
      className: "middle-roll-2",
    },
    action: targetIfUserTakesControl("zoom_check"),
  },
  {
    enableZaunSelection: false,
    dialog: zoomDialog,
    mouseAnimation: {
      action: async (stepEngine) => await stepEngine.mouse.zoomAction(1, 300),
      className: "middle-roll-1",
    },
    action: targetIfUserTakesControl("zoom_check"),
  },
  {
    enableZaunSelection: false,
    dialog: zoomDialog,
    mouseAnimation: {
      action: async (stepEngine) => await stepEngine.mouse.zoomAction(0.7, 1000),
      className: "middle-roll-2",
    },
    action: targetIfUserTakesControl("zoom_check"),
  },
  {
    enableZaunSelection: false,
    dialog: zoomDialog,
    mouseAnimation: {
      action: async (stepEngine) => await stepEngine.mouse.zoomAction(1 / (0.7 * 0.7), 1000),
      className: "middle-roll-1",
    },
    action: targetIfUserTakesControl("zoom_check"),
  },
  {
    enableZaunSelection: false,
    dialog: zoomDialog,
    mouseAnimation: {
      action: async (stepEngine) => await stepEngine.mouse.zoomAction(0.7, 1000),
      className: "middle-roll-2",
    },
    action: targetIfUserTakesControl("zoom_check"),
  },
  {
    name: "zoom_check",
    enableZaunSelection: false,
    dialog: {
      key: "zoom",
      text: () => <p>{I18N("tutorial_Zoomen Sie jetzt bitte die Zaunanlage")}</p>,
      buttons: [ButtonTutorialÜberspringen, { ...ButtonAnimationNochmal, target: "zoom" }],
    },
    action: async (stepEngine) => {
      const cleanup = stepEngine.mouse.orbitControlsSolvedAction(
        [
          APP.controls.STATE.DOLLY,
          APP.controls.STATE.TOUCH_DOLLY_PAN,
          APP.controls.STATE.TOUCH_DOLLY_ROTATE,
        ],
        4,
        () => stepEngine.setStep("passed")
      );
      stepEngine.onStateExit(cleanup);
    },
    target: null,
  },
  {
    name: "passed",
    // duration: 4000,
    enableZaunSelection: false,
    dialog: {
      text: () => (
        <p>
          {I18N("tutorial_Sie haben die Zaunplaner-Führerscheinprüfung mit Bravour bestanden")} 😊{" "}
          <br />
          <br />
          {I18N("tutorial_Wenn Sie weitere Hilfe benötigen, klicken sie mich einfach an")}
        </p>
      ),
      buttons: [
        {
          label: () => I18N("tutorial_Alles klar!"),
          className: "primary-button",
          autoFocus: true,
          target: "final",
        },
      ],
    },
  },
  {
    name: "final",
    target: null,
  },
];
